// Phase 6 — Dedicated mobile cricket flyer (V4 only). Standalone
// design — not a rebrand of the desktop Cricket V3 broadcast
// layout. Aurora gradient background, big stacked "HOME vs AWAY"
// hero, compact one-column player list with skill chips, full-
// width volunteers card, reporting-time ribbon, and brand footer.
// Lives alongside CricketFlyerV3 so desktop V1/V2/V3 renders the
// original; V4 swaps in this component instead.

import React from "react";
import { Star, CalendarDays, Clock, MapPin } from "lucide-react";
import { SKILL_BY_CODE } from "../../data";
import {
  playerSkillCodes, TeamCrest, teamLogoSrc,
  formatDateTemplate, formatTimeTemplate
} from "./flyerHelpers";

export function CricketFlyerMobile({
  match, opp, homeTeamName, homeAcronym, footerBrand,
  team, rules, startingPlayers, reportingTime,
  venueShort, venueAddress,
  showVolunteers, snacksVolunteer, alcoholVolunteer,
  template = null
}) {
  // League subheading — Rowdys plays in the "Pittsburgh Cricket
  // League"; everyone else (Pitt Gabruz, fallback) plays in the
  // "Pittsburgh Premier Cricket League". For Rowdys we force the
  // default and IGNORE any leagueLine on the template (the template
  // is Pitt Gabruz's, borrowed for colour palette only).
  const isRowdys = team?.code === "ROWDYS_CRICKET";
  const defaultLeagueLine = isRowdys
    ? "PITTSBURGH CRICKET LEAGUE"
    : "PITTSBURGH PREMIER CRICKET LEAGUE";
  const leagueLine = isRowdys
    ? defaultLeagueLine
    : (template?.leagueLine || defaultLeagueLine);
  const showSkills    = template?.showSkills !== false;
  const useVolunteers = showVolunteers && (template?.showVolunteers !== false);

  // For Rowdys, Squads.jsx swaps in Pitt Gabruz's flyer template so
  // the colour combination matches Pitt Gabruz's flyer exactly. This
  // component just applies whatever template it receives.
  const primaryColor = template?.primaryColor || null;
  const bgTone       = template?.bgTone       || null;

  const rootStyle = {};
  if (primaryColor) rootStyle["--cfm-gold"] = primaryColor;
  if (bgTone === "darker")  rootStyle["--cfm-bg-deep"] = "#04060e";
  if (bgTone === "lighter") rootStyle["--cfm-bg-deep"] = "#121b34";

  return (
    <div className="poster poster--cricketMobile" style={rootStyle}>
      {/* Decorative team logo as a low-opacity background watermark.
          Captured by html2canvas so it appears in the downloaded PNG. */}
      <img
        className="cfm-watermark"
        src={teamLogoSrc(team)}
        alt=""
        aria-hidden="true"
        crossOrigin="anonymous"
        onError={e => e.currentTarget.style.display = "none"}
      />

      {/* ── HEADER ─────────────────────────────────────────── */}
      <header className="cfm-header">
        <div className="cfm-stars">
          <Star size={11} fill="currentColor" stroke="none"/>
          <Star size={15} fill="currentColor" stroke="none"/>
          <Star size={11} fill="currentColor" stroke="none"/>
        </div>
        <h1 className="cfm-brand">GABRUZ UNIVERSE</h1>
        <div className="cfm-leaguePill">
          <span className="cfm-leagueChev">»</span>
          {leagueLine}
          <span className="cfm-leagueChev">«</span>
        </div>
      </header>

      {/* ── MATCH HERO ──────────────────────────────────────
          Layout:
            row 1 — HOME team |  LOGO  | AWAY team
            row 2 —              VS  (centered)
            row 3 — Match Day | Reporting | Ground chips (3 cols) */}
      <article className="cfm-matchCard">
        <div className="cfm-matchCard-hero">
          <div className="cfm-matchCard-team cfm-matchCard-team--home">
            <small>HOME</small>
            <b>{homeTeamName}</b>
          </div>
          <div className="cfm-matchCard-logoRing">
            <TeamCrest team={team} fallbackName={homeAcronym} className="cfm-matchCard-logo"/>
          </div>
          <div className="cfm-matchCard-team cfm-matchCard-team--away">
            <small>AWAY</small>
            <b>{opp}</b>
          </div>
        </div>

        <div className="cfm-matchCard-vsRow">
          <span className="cfm-matchCard-vsLabel">VS</span>
        </div>

        <div className="cfm-matchCard-chips">
          <div className="cfm-matchCard-chip">
            <CalendarDays size={14}/>
            <div>
              <small>MATCH DAY</small>
              <b>{formatDateTemplate(match?.date)}</b>
            </div>
          </div>
          <div className="cfm-matchCard-chip">
            <Clock size={14}/>
            <div>
              <small>REPORTING</small>
              <b>{formatTimeTemplate(reportingTime)}</b>
            </div>
          </div>
          <div className="cfm-matchCard-chip">
            <MapPin size={14}/>
            <div>
              <small>GROUND</small>
              <b>{venueShort}</b>
              {venueAddress && <em>{venueAddress}</em>}
            </div>
          </div>
        </div>
      </article>

      {/* ── SQUAD ─────────────────────────────────────────── */}
      {/* Iterate the actual startingPlayers array — no empty
          placeholder rows when the squad has fewer than the max. */}
      <section className="cfm-squad">
        <ol className="cfm-squad-list">
          {startingPlayers.map((p, i) => {
            const codes = playerSkillCodes(team, p.id);
            return (
              <li key={p.id || i} className="cfm-squad-row">
                <span className="cfm-squad-rowNum">{String(i + 1).padStart(2, "0")}</span>
                <span className="cfm-squad-rowName">{p.name}</span>
                {showSkills && codes.length > 0 && (
                  <span className="cfm-squad-rowSkills">
                    {codes.slice(0, 3).map(c => {
                      const skill = SKILL_BY_CODE[c];
                      return (
                        <em
                          key={c}
                          className={`cfm-squad-skill cfm-squad-skill--${skill?.category || "OTHER"}`}
                          title={skill?.label}
                        >{skill?.short || c}</em>
                      );
                    })}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      {/* ── VOLUNTEERS ──────────────────────────────────────
          The "VOLUNTEERS" header label is intentionally omitted;
          the two slots speak for themselves. */}
      {useVolunteers && (
        <section className="cfm-volunteers">
          <div className="cfm-volunteers-grid">
            <div className="cfm-volunteers-slot">
              <small>WATER / GATORADE</small>
              <b>{snacksVolunteer || "— TBD —"}</b>
            </div>
            <div className="cfm-volunteers-slot">
              <small>BANANAS / PROTEIN</small>
              <b>{alcoholVolunteer || "— TBD —"}</b>
            </div>
          </div>
        </section>
      )}

      {/* ── REPORTING STRIP ──────────────────────────────── */}
      <div className="cfm-report">
        <Clock size={20} className="cfm-report-icon"/>
        <div>
          <small>BE AT THE GROUND</small>
          <b>NO LATER THAN {formatTimeTemplate(reportingTime)}</b>
        </div>
      </div>
    </div>
  );
}
